import { formatFileSize, isAllowedFile, ProxyFormComponent } from '@theme/proxy-form';

const LIMIT_FALLBACKS = {
  maxPhotoCount: 8,
  maxPhotoSize: 10 * 1048576,
  maxAttachmentTotal: 20 * 1048576,
};

const DOCUMENT_EXTENSIONS = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.csv'];
const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
];

/** @typedef {'count' | 'type' | 'size' | 'total'} PhotoRejectReason */
/** @typedef {'type' | 'size' | 'total'} DocumentRejectReason */

class SellFormComponent extends ProxyFormComponent {
  requiredRefs = ['tabs', 'panels', 'rowsContainer', 'rowTemplate', 'builderFields'];

  errorScope(form) {
    return form.closest('[role="tabpanel"]') || form;
  }

  resolveErrorSlot(key) {
    if (key === 'photos' || key.startsWith('photos.')) return 'photos';
    if (key === 'items' || key.startsWith('items.')) return 'items';
    return key;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const entries of this.#photoEntries.values()) {
      for (const entry of entries) URL.revokeObjectURL(entry.url);
    }
    this.#photoEntries.clear();
  }

  /**
   * @param {HTMLElement} slot
   * @param {'count' | 'type' | 'size' | 'total'} reason
   */
  #slotMessage(slot, reason) {
    /** @type {Record<typeof reason, keyof DOMStringMap>} */
    const keys = {
      count: 'msgCount',
      type: 'msgType',
      size: 'msgSize',
      total: 'msgTotal',
    };

    return slot.dataset[keys[reason]] || '';
  }

  selectTab(event) {
    const index = (this.refs.tabs || []).indexOf(event.target);
    if (index < 0) return;
    this.#activateTab(index);
  }

  #activateTab(index) {
    for (const [i, tab] of (this.refs.tabs || []).entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of (this.refs.panels || []).entries()) {
      panel.toggleAttribute('inert', i !== index);
    }
  }

  get #itemInputs() {
    return Array.from(this.refs.builderFields.querySelectorAll('[data-field]'));
  }

  addRow() {
    for (const input of this.#itemInputs) {
      if (input instanceof HTMLInputElement && input.dataset.validate === 'vintage') {
        this.#syncMax(input);
        this.#setVintageValidity(input);
        if (input.value.trim()) input.value = this.#normalizeVintage(input.value);
      }
      if (!input.reportValidity()) return;
    }

    const fragment = this.refs.rowTemplate.content.cloneNode(true);
    const row = fragment.querySelector('.sell-form__row');
    if (!row) return;

    for (const input of this.#itemInputs) {
      const cell = row.querySelector(`[data-value="${input.dataset.field}"]`);
      if (cell) cell.textContent = input.value;
    }

    this.refs.rowsContainer.append(fragment);
    this.#resetItemInputs();
  }

  editRow(event) {
    const row = event.target.closest('.sell-form__row');
    if (!row) return;

    for (const input of this.#itemInputs) {
      const cell = row.querySelector(`[data-value="${input.dataset.field}"]`);
      if (cell) input.value = cell.textContent;
    }

    row.remove();
    this.#itemInputs[0]?.focus();
  }

  removeRow(event) {
    event.target.closest('.sell-form__row')?.remove();
  }

  #resetItemInputs() {
    for (const input of this.#itemInputs) {
      if (input instanceof HTMLSelectElement) {
        input.selectedIndex = 0;
      } else {
        input.value = '';
      }
      if (input instanceof HTMLInputElement && input.dataset.validate) {
        input.setCustomValidity('');
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  #syncMax(input) {
    input.dataset.max = String(Math.max(Number(input.dataset.max), new Date().getFullYear()));
  }

  /**
   * @param {HTMLInputElement} input
   */
  #setVintageValidity(input) {
    const value = input.value.trim();
    if (!value) {
      input.setCustomValidity('');
      return;
    }

    const isValid =
      /^nv$/i.test(value) ||
      (/^\d{4}$/.test(value) &&
        Number(value) >= Number(input.dataset.min) &&
        Number(value) <= Number(input.dataset.max));

    input.setCustomValidity(isValid ? '' : input.title);
  }

  /**
   * @param {string} value
   */
  #normalizeVintage(value) {
    const trimmed = value.trim();
    return /^nv$/i.test(trimmed) ? 'NV' : trimmed;
  }

  #photoEntries = new Map();

  triggerPhotoInput(event) {
    event.target.closest('.proxy-form__upload-box')?.querySelector('[data-role="photo-input"]')?.click();
  }

  handlePhotosChange(event) {
    const input = event.target;
    let entries = this.#photoEntries.get(input);
    if (!entries) {
      entries = [];
      this.#photoEntries.set(input, entries);
    }

    const form = input.closest('form');
    const documentBytes = this.#getDocumentSize(form);
    const maxPhotoCount = this.getLimit('maxPhotoCount', LIMIT_FALLBACKS.maxPhotoCount);
    const maxPhotoSize = this.getLimit('maxPhotoSize', LIMIT_FALLBACKS.maxPhotoSize);
    const maxAttachmentTotal = this.getLimit('maxAttachmentTotal', LIMIT_FALLBACKS.maxAttachmentTotal);

    /** @type {PhotoRejectReason | null} */
    let rejectReason = null;
    let runningTotal = entries.reduce((sum, entry) => sum + entry.file.size, 0);

    for (const file of input.files) {
      if (entries.length >= maxPhotoCount) {
        rejectReason = 'count';
        break;
      }

      if (!file.type.startsWith('image/')) {
        if (!rejectReason) rejectReason = 'type';
        continue;
      }

      if (file.size > maxPhotoSize) {
        if (!rejectReason) rejectReason = 'size';
        continue;
      }

      if (runningTotal + documentBytes + file.size > maxAttachmentTotal) {
        rejectReason = 'total';
        break;
      }

      entries.push({ file, url: URL.createObjectURL(file) });
      runningTotal += file.size;
    }

    input.value = '';
    this.#renderPhotoPreviews(input);

    const box = input.closest('.proxy-form__upload-box');
    if (rejectReason) {
      this.#showPhotoError(box, rejectReason);
    } else {
      this.#clearPhotoError(box);
    }
  }

  removePhoto(event) {
    const item = event.target.closest('.sell-form__photo-preview');
    const box = event.target.closest('.proxy-form__upload-box');
    const input = box?.querySelector('[data-role="photo-input"]');
    if (!item || !input) return;

    const entries = this.#photoEntries.get(input) || [];
    const index = Number(item.dataset.index);
    const [removed] = entries.splice(index, 1);
    if (removed) URL.revokeObjectURL(removed.url);

    this.#renderPhotoPreviews(input);
    this.#clearPhotoError(box);
  }

  /**
   * @param {HTMLElement | null | undefined} slot
   * @param {string} message
   */
  #showFieldError(slot, message) {
    if (!slot) return;
    slot.textContent = message;
  }

  /** @param {HTMLElement | null | undefined} slot */
  #clearFieldError(slot) {
    this.#showFieldError(slot, '');
  }

  /**
   * @param {HTMLElement | null | undefined} box
   * @param {PhotoRejectReason} reason
   */
  #showPhotoError(box, reason) {
    const slot = box?.querySelector('[data-error-for="photos"]');
    if (!(slot instanceof HTMLElement)) return;
    this.#showFieldError(slot, this.#slotMessage(slot, reason));
  }

  /** @param {HTMLElement | null | undefined} box */
  #clearPhotoError(box) {
    const slot = box?.querySelector('[data-error-for="photos"]');
    if (!(slot instanceof HTMLElement)) return;
    this.#clearFieldError(slot);
  }

  /**
   * @param {HTMLElement | null | undefined} box
   * @param {DocumentRejectReason} reason
   */
  #showDocumentError(box, reason) {
    const slot = box?.querySelector('[data-error-for="items_document"]');
    if (!(slot instanceof HTMLElement)) return;
    this.#showFieldError(slot, this.#slotMessage(slot, reason));
  }

  /** @param {HTMLElement | null | undefined} box */
  #clearDocumentError(box) {
    const slot = box?.querySelector('[data-error-for="items_document"]');
    if (!(slot instanceof HTMLElement)) return;
    this.#clearFieldError(slot);
  }

  #renderPhotoPreviews(input) {
    const box = input.closest('.proxy-form__upload-box');
    const previews = box?.querySelector('[data-role="photo-previews"]');
    if (!previews) return;

    const template = box.querySelector('[data-role="photo-preview-template"]');
    const entries = this.#photoEntries.get(input) || [];
    previews.replaceChildren();

    for (const [index, entry] of entries.entries()) {
      const fragment = template.content.cloneNode(true);
      const item = fragment.querySelector('.sell-form__photo-preview');
      item.dataset.index = String(index);

      const image = item.querySelector('img');
      image.src = entry.url;
      image.alt = entry.file.name;

      previews.append(fragment);
    }

    previews.hidden = entries.length === 0;
  }

  #photoFilesFor(form) {
    const input = form.querySelector('[data-role="photo-input"]');
    if (!input) return [];
    return (this.#photoEntries.get(input) || []).map((entry) => entry.file);
  }

  #resetPhotos(form) {
    const input = form.querySelector('[data-role="photo-input"]');
    if (!input) return;

    for (const entry of this.#photoEntries.get(input) || []) {
      URL.revokeObjectURL(entry.url);
    }

    this.#photoEntries.set(input, []);
    this.#renderPhotoPreviews(input);
    this.#clearPhotoError(input.closest('.proxy-form__upload-box'));
  }

  triggerDocumentInput(event) {
    event.target.closest('.proxy-form__upload-box')?.querySelector('[data-role="document-input"]')?.click();
  }

  handleDocumentChange(event) {
    const input = event.target;
    const box = input.closest('.proxy-form__upload-box');
    const file = input.files?.[0];
    if (!file) {
      this.#resetDocument(box);
      this.#clearDocumentError(box);
      return;
    }

    const form = input.closest('form');
    const maxAttachmentTotal = this.getLimit('maxAttachmentTotal', LIMIT_FALLBACKS.maxAttachmentTotal);
    const photosInput = form?.querySelector('[data-role="photo-input"]');
    const photosBytes = this.#getPhotosBytes(photosInput);

    if (!isAllowedFile(file, DOCUMENT_EXTENSIONS, DOCUMENT_MIME_TYPES)) {
      this.#resetDocument(box);
      this.#showDocumentError(box, 'type');
      return;
    }

    if (file.size > maxAttachmentTotal) {
      this.#resetDocument(box);
      this.#showDocumentError(box, 'size');
      return;
    }

    if (file.size + photosBytes > maxAttachmentTotal) {
      this.#resetDocument(box);
      this.#showDocumentError(box, 'total');
      return;
    }

    const info = box?.querySelector('[data-role="document-info"]');
    const name = box?.querySelector('[data-role="document-name"]');
    if (!(info instanceof HTMLElement) || !name) return;

    name.textContent = `${file.name} (${formatFileSize(file.size)})`;
    info.hidden = false;
    this.#clearDocumentError(box);
  }

  removeDocument(event) {
    const box = event.target.closest('.proxy-form__upload-box');
    this.#resetDocument(box);
    this.#clearDocumentError(box);
  }

  /** @param {HTMLElement | null | undefined} box */
  #resetDocument(box) {
    const input = box?.querySelector('[data-role="document-input"]');
    const info = box?.querySelector('[data-role="document-info"]');
    if (input instanceof HTMLInputElement) input.value = '';
    if (info instanceof HTMLElement) info.hidden = true;
  }

  /**
   * @param {HTMLFormElement | null} form
   */
  #getDocumentSize(form) {
    if (!form) return 0;
    const input = form.querySelector('[data-role="document-input"]');
    if (!(input instanceof HTMLInputElement)) return 0;
    return input.files[0]?.size ?? 0;
  }

  /**
   * @param {HTMLInputElement | null | undefined} input
   */
  #getPhotosBytes(input) {
    if (!input) return 0;
    return (this.#photoEntries.get(input) || []).reduce((sum, entry) => sum + entry.file.size, 0);
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target.closest('form');
    if (!form) return;

    const mode = form.dataset.mode;
    const documentFile = form.querySelector('[data-role="document-input"]')?.files[0];
    const photoFiles = this.#photoFilesFor(form);

    const formData = this.#buildFormData(form, mode, documentFile, photoFiles);
    await this.submitToProxy(form, this.dataset.handle, formData);
  }

  #buildFormData(form, mode, documentFile, photoFiles) {
    const formData = new FormData(form);
    formData.set('form_type', this.dataset.productType);
    formData.set('mode', mode);

    if (mode === 'builder') {
      for (const [index, row] of Array.from(this.refs.rowsContainer.children).entries()) {
        for (const cell of row.querySelectorAll('[data-value]')) {
          formData.set(`items[${index}][${cell.dataset.value}]`, cell.textContent);
        }
      }
    }

    if (mode === 'upload' && documentFile) {
      formData.set('items_document', documentFile);
    }

    for (const file of photoFiles) {
      formData.append('photos[]', file);
    }

    return formData;
  }

  onProxySuccess(form) {
    this.#resetForm(form, form.dataset.mode);
    this.querySelector('dialog-component')?.showDialog();
  }

  #resetForm(form, mode) {
    form.reset();
    this.#resetPhotos(form);

    if (mode === 'builder') {
      this.refs.rowsContainer.replaceChildren();
      this.#resetItemInputs();
    }

    if (mode === 'upload') {
      this.#resetDocument(form.querySelector('[data-role="document-input"]')?.closest('.proxy-form__upload-box'));
    }
  }
}

if (!customElements.get('sell-form-component')) {
  customElements.define('sell-form-component', SellFormComponent);
}
